import { create } from "zustand";
import {
  buildWindows,
  loadDeviceHistory,
  processInBatches,
  DEVICE_CONCURRENCY,
} from "../../pages/reports/utils/trackingHistoryWindows";
import {
  buildStops,
  toStopPoint,
  type StopBucket,
} from "../../pages/reports/stop-section/stopsReportRule";

export type { StopBucket };

export const STOPS_COLORS = [
  "#2563EB",
  "#DB2777",
  "#16A34A",
  "#D97706",
  "#7C3AED",
  "#0891B2",
  "#DC2626",
  "#65A30D",
];

export interface StopsResult {
  deviceId: string;
  vehicleName: string;
  color: string;
  totalParadas: number;
  buckets: Record<StopBucket, number>;
  tiempoTotalSeconds: number;
  loading: boolean;
  error: string | null;
}

export interface StopsDevice {
  deviceId: string;
  vehicleName: string;
}

interface ReportStopsState {
  results: Record<string, StopsResult>;
  order: string[];
  loading: boolean;
  from: string | null;
  to: string | null;
  tz: string | null;
}

interface ReportStopsActions {
  loadRange: (
    devices: StopsDevice[],
    from: string,
    to: string,
    opts?: { tz?: string },
  ) => Promise<void>;
  clear: () => void;
}

type ReportStopsStore = ReportStopsState & ReportStopsActions;

export const useReportStopsStore = create<ReportStopsStore>()((set) => ({
  results: {},
  order: [],
  loading: false,
  from: null,
  to: null,
  tz: null,

  loadRange: async (devices, from, to, opts) => {
    const resolvedTz = opts?.tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const order = devices.map((d) => d.deviceId);

    const fromMs = new Date(from).getTime();
    const toMs = new Date(to).getTime();
    const windows = buildWindows(fromMs, toMs);

    const initial: Record<string, StopsResult> = {};
    devices.forEach((d, i) => {
      initial[d.deviceId] = {
        deviceId: d.deviceId,
        vehicleName: d.vehicleName,
        color: STOPS_COLORS[i % STOPS_COLORS.length],
        totalParadas: 0,
        buckets: { "0_5": 0, "5_15": 0, "15_30": 0, "30_60": 0, "60_mas": 0 },
        tiempoTotalSeconds: 0,
        loading: true,
        error: null,
      };
    });

    set({ results: initial, order, loading: true, from, to, tz: resolvedTz });

    await processInBatches(devices, DEVICE_CONCURRENCY, async (d) => {
      const { points, failedChunks } = await loadDeviceHistory(
        d.deviceId,
        windows,
        resolvedTz,
      );

      const parsed = points.filter((p) => p.ts != null).map(toStopPoint);
      const { buckets, totalParadas, tiempoTotalSeconds } = buildStops(parsed);

      set((state) => {
        const current = state.results[d.deviceId];
        if (!current) return state;
        return {
          results: {
            ...state.results,
            [d.deviceId]: {
              ...current,
              totalParadas,
              buckets,
              tiempoTotalSeconds,
              loading: false,
              error:
                parsed.length === 0 && failedChunks > 0
                  ? "No se pudieron obtener las paradas (servicio no disponible)."
                  : parsed.length === 0
                    ? "Sin datos en el rango."
                    : failedChunks > 0
                      ? `${failedChunks} tramo(s) con error`
                      : null,
            },
          },
        };
      });
    });

    set({ loading: false });
  },

  clear: () =>
    set({ results: {}, order: [], loading: false, from: null, to: null, tz: null }),
}));
